// TOP LEVEL IMPORTS
import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
// ROUTE CONTAINERS
import PublicRoute from 'components/route-components/PublicRoute'; // will redirect them to home screen if signed in
import ProtectedRoute from 'components/route-components/ProtectedRoute'; // will redirect them to login if signed out
import BlankRoute from 'components/route-components/BlankRoute'; // will just render whatever you give it, agnostic to if user is authenticated or not
import AdminRoute from 'components/route-components/AdminRoute';
// LAYOUTS
import PublicLayout from 'components/layout/PublicLayout';
import AppLayout from 'components/layout/AppLayout';
import BlankLayout from 'components/layout/BlankLayout';
import AdminLayout from 'components/layout/AdminLayout';
// COMPONENTS
import OfflinePlaceholder from 'components/common/OfflinePlaceholder';
import LoadingScreen from 'components/common/LoadingScreen';
import EmptyState from 'components/common/EmptyState';
// ROUTES
import AdminHomeRoute from 'routes/admin-home';
import AdminTopUsersRoute from 'routes/admin-top-users';
import AdminQuestionsRoute from 'routes/admin-questions';
import AdminUserDetailRoute from 'routes/admin-user-detail';
import AdminStudiosRoute from 'routes/admin-studios';
import AdminUsersRoute from 'routes/admin-users';
import AuthLoginRoute from 'routes/auth-login';
import AppHomeRoute from 'routes/app-home';
import ContractDraftRoute from 'routes/contract-draft';
import InvoiceCreateRoute from 'routes/invoice-create';
import InvoicePayRoute from 'routes/invoice-pay';
import ClientInviteRoute from 'routes/client-invite';
import AppClientsRoute from 'routes/app-clients';
import AppClientDetailRoute from 'routes/app-client-detail';
import AppCalendarRoute from 'routes/app-calendar';
import AppStudioRoute from 'routes/app-studio';
import AppAccount from 'routes/app-account';
import PageNotFound from 'routes/not-found';
import PasswordResetRoute from 'routes/auth-password-reset';
import ForgotPasswordRoute from 'routes/auth-forgot-password';
import EnrollAccountRoute from 'routes/auth-enroll-account';
import ContractSignRoute from 'routes/contract-sign';
import ContractViewRoute from 'routes/contract-view';
import GoogleAuthCallbackRoute from 'routes/app-google-auth-callback';
import PaypalAuthCallbackRoute from 'routes/app-paypal-auth-callback';
import ClientQuestionnairePreviewRoute from 'routes/client-questionnaire-preview';
import ClientQuestionnaireRoute from 'routes/client-questionnaire';

// LIB
import styled from 'styled-components';
// APOLLO
import { compose, graphql } from 'react-apollo';
import currentUserQuery from 'ApolloClient/Queries/currentUser';

// CONSTANTS & DESTRUCTURING
// ========================================

// HELPER FUNCTIONS
// ========================================
// for these errors, we don't want to show the error/empty state, we want the user to go to the login screen.
const isWhiteListError = errorMessage => {
  let result = false;
  let WHITE_LISTED_ERRORS = ['Signature verification failed', 'JWTExtendedException', 'Missing JWT'];
  // for each through each error and see if the errorMessage is one of our whiteListErrors...
  // if it's true, isWhiteListError returns true,
  // and the ui will not display an error but rather let user get booted to the login screen
  WHITE_LISTED_ERRORS.forEach(whiteListError => {
    if (errorMessage.includes(whiteListError)) {
      result = true;
    }
  });
  return result;
};

// STYLED-COMPONENTS
// ========================================
const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ddd;
`;

const AppContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const ConnectionIssue = () => (
  <Container>
    <EmptyState
      title="We seem to be having connection issues..."
      subtitle="Don’t worry. This page will automatically reconnect soon."
    />
  </Container>
);

// EXPORTED COMPONENT
// ========================================
class AppRoutes extends React.Component {
  render() {
    const { loading, error, currentUser } = this.props.currentUserQuery;

    if (loading) {
      return <LoadingScreen theme={this.props.theme} />;
    }

    // show specific message for certain network/timeout errors
    if (
      error
      && error.message
      && (error.message.includes('Timeout exceeded') || error.message.includes('ConnectionTimeout'))
    ) {
      return <ConnectionIssue />;
    }

    // all other errors (that are not in our white listed errors list) just print to screen for now, unless it's a white listed error
    if (error && error.message && !isWhiteListError(error.message)) {
      return (
        <Container>
          <EmptyState title={error.message} />
        </Container>
      );
    }

    return (
      <AppContainer>
        <React.Fragment>
          <OfflinePlaceholder />
          <Router>
            <Switch>
              <PublicRoute
                layout={PublicLayout}
                currentUser={currentUser}
                path="/recover-password/:token"
                component={PasswordResetRoute}
              />
              <BlankRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/blank/complete-questionnaire/:questionTemplateId" // keeping /blank/ in the URL so we are backwards compatible.
                component={ClientQuestionnaireRoute}
              />
              <BlankRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/questionnaire-preview/:questionTemplateId"
                component={ClientQuestionnairePreviewRoute}
              />
              <PublicRoute
                layout={PublicLayout}
                exact
                currentUser={currentUser}
                path="/forgot-password"
                component={ForgotPasswordRoute}
              />
              <BlankRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/contract/view/:_id"
                component={ContractViewRoute}
              />
              <PublicRoute
                layout={PublicLayout}
                currentUser={currentUser}
                path="/enroll-account/:token"
                component={EnrollAccountRoute}
              />
              <ProtectedRoute
                layout={AppLayout}
                currentUser={currentUser}
                path="/app/clients/:_id"
                component={AppClientDetailRoute}
              />
              <BlankRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/contract/sign/:_id"
                component={ContractSignRoute}
              />
              {/* e.g. https://heyned-staging.netlify.com/contract/sign/QfuxPKtQggvTfYNEh?type=client */}
              <ProtectedRoute
                layout={AppLayout}
                currentUser={currentUser}
                path="/app/clients"
                component={AppClientsRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/users/:userId"
                component={AdminUserDetailRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/users"
                component={AdminUsersRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/users"
                component={AdminUsersRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/studios"
                component={AdminStudiosRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/top-users"
                component={AdminTopUsersRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/questions"
                component={AdminQuestionsRoute}
              />

              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin/studios"
                component={AdminStudiosRoute}
              />
              <AdminRoute
                layout={AdminLayout}
                exact
                currentUser={currentUser}
                path="/admin"
                component={AdminHomeRoute}
              />

              <ProtectedRoute
                layout={AppLayout}
                currentUser={currentUser}
                path="/app/studio"
                component={AppStudioRoute}
              />
              <ProtectedRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/app/contract/draft/:contractId"
                component={ContractDraftRoute}
              />
              <ProtectedRoute
                layout={AppLayout}
                currentUser={currentUser}
                path="/app/calendar"
                component={AppCalendarRoute}
              />
              <ProtectedRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/invoice/create/1/:invoiceId"
                component={InvoiceCreateRoute}
              />
              <ProtectedRoute
                layout={AppLayout}
                currentUser={currentUser}
                path="/app/google-auth-callback"
                component={GoogleAuthCallbackRoute}
              />
              <ProtectedRoute
                layout={AppLayout}
                currentUser={currentUser}
                path="/app/paypal-auth-callback"
                component={PaypalAuthCallbackRoute}
              />
              <PublicRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/invoice/pay/:_id"
                component={InvoicePayRoute}
              />
              <PublicRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/invoice/paypal-return/:_id"
                component={InvoicePayRoute}
              />
              {/* new route for the client invite form */}
              <BlankRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/studio/client-invite/:_id"
                component={ClientInviteRoute}
              />
              {/* backwards compatiable to still support our old route for the client invite form */}
              <BlankRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/blank/new-client/:_id"
                component={ClientInviteRoute}
              />
              <PublicRoute
                layout={BlankLayout}
                currentUser={currentUser}
                path="/invoice/paypal-canceled"
                component={InvoicePayRoute}
              />
              <ProtectedRoute layout={AppLayout} currentUser={currentUser} path="/app/account" component={AppAccount} />
              <ProtectedRoute layout={AppLayout} currentUser={currentUser} path="/app" component={AppHomeRoute} />
              <PublicRoute
                exact
                layout={PublicLayout}
                currentUser={currentUser}
                path="/login"
                component={AuthLoginRoute}
              />
              <PublicRoute exact layout={PublicLayout} currentUser={currentUser} path="/" component={AuthLoginRoute} />
              <Route
                render={() => (
                  <PublicLayout>
                    <PageNotFound />
                  </PublicLayout>
                )}
              />
            </Switch>
          </Router>
        </React.Fragment>
      </AppContainer>
    );
  }
}

// APOLLO
// ==============================
let GET_USER_QUERY_NAME = 'currentUserQuery';

let options = () => ({
  pollInterval: 300000, // rerun currentUser every 5 minutes
});

// EXPORT
// ==============================
export default compose(graphql(currentUserQuery, { options, name: GET_USER_QUERY_NAME }))(AppRoutes);



// WEBPACK FOOTER //
// ./src/routes/index.js